import React from 'react'
import {
	Box, Card, CardHeader, Typography, Table, TableHead, TableBody, TableRow, TableCell,
	ToggleButton, ToggleButtonGroup, Switch, FormControl, InputLabel, Select, MenuItem,
	IconButton, Grid, Divider, Popover
} from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'
import { demoPopoverOpen } from './signals'


const DEMO = [
	{ name: 'alphacars', flag: '🇬🇧', prices: [165.20, 172.88, 169.41, 178.55] },
	{ name: 'peakrentals', flag: '🇬🇧', prices: [98.45, 102.33, 101.10, 107.22] },
	{ name: 'eurodrive', flag: '🇬🇧', prices: [101.30, 105.77, 103.95, 112.60] },
	{ name: 'horizoncars', flag: '🇬🇧', prices: [103.88, 106.38, 108.50, 115.20] },
	{ name: 'swiftfleet', flag: '🇬🇧', prices: [99.70, 106.98, 104.25, 110.45] },
]

const DATES = ['Mon 12 May', 'Wed 14 May', 'Fri 16 May', 'Mon 19 May']
const COLORS = ['#ef5350', '#66bb6a', '#42a5f5', '#ffa726', '#ab47bc']


export default function DorDemoData() {
	// Reactive: reads signal so component re-renders when demoPopoverOpen changes
	const anchorEl = demoPopoverOpen.value
		? document.querySelector('[data-tour-id="tour-price-cell"]')
		: null

	const mins = DATES.map((_, i) => Math.min(...DEMO.map(d => d.prices[i])))
	const maxs = DATES.map((_, i) => Math.max(...DEMO.map(d => d.prices[i])))

	const cellSx = (price, colIdx) => ({
		textAlign: 'center', p: '10px 14px',
		...(price === mins[colIdx] && { background: '#e8f5e9', border: '2px solid #a5d6a7', borderRadius: 1 }),
		...(price === maxs[colIdx] && { background: '#ffebee', border: '2px solid #ef9a9a', borderRadius: 1 }),
	})

	return <Grid container spacing={2}>
		<Grid item xs={12}>
			<Card>
				<CardHeader title={
					<Typography variant="subtitle1" fontWeight={600}>Analyzer: Price Table</Typography>
				} />

				<Box sx={{ px: 3, pb: 1, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Typography variant="body2" fontWeight={500}>Hide Details</Typography>
						<Switch size="small" disabled />
					</Box>

					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
							<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#66bb6a' }} />
							<Typography variant="caption" color="text.secondary">Cheapest Price in column</Typography>
						</Box>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
							<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef5350' }} />
							<Typography variant="caption" color="text.secondary">Most Expensive Price in column</Typography>
						</Box>
					</Box>

					<FormControl size="small" sx={{ minWidth: 140 }}>
						<InputLabel>Features</InputLabel>
						<Select value="" label="Features" disabled>
							<MenuItem value="">Any feature</MenuItem>
						</Select>
					</FormControl>

					<ToggleButtonGroup size="small" exclusive value="min">
						<ToggleButton value="min">Min</ToggleButton>
						<ToggleButton value="max">Max</ToggleButton>
					</ToggleButtonGroup>
				</Box>

				<Box sx={{ px: 3, pb: 2, overflowX: 'auto' }}>
					<Table size="small">
						<TableHead>
							<TableRow sx={{ '& th': { bgcolor: 'primary.main', color: 'white', fontWeight: 600 } }}>
								<TableCell>Website</TableCell>
								{DATES.map((d, i) => (
									<TableCell key={d} align="center" {...(i === 0 ? { 'data-tour-id': 'tour-column-header' } : {})}>
										{d}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{DEMO.map((d, rowIdx) => (
								<TableRow key={d.name}>
									<TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{d.flag} {d.name}</TableCell>
									{d.prices.map((p, i) => (
										<TableCell
											key={i}
											sx={cellSx(p, i)}
											{...(rowIdx === 0 && i === 0 ? { 'data-tour-id': 'tour-price-cell' } : {})}
										>
											<Typography variant="body2" fontWeight={500}>
												{p.toFixed(2)} <Typography component="span" variant="caption" color="text.secondary">≐ 0%</Typography>
											</Typography>
											<Typography variant="caption" color="text.secondary">{p.toFixed(2)}</Typography>
										</TableCell>
									))}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			</Card>
		</Grid>

		<Grid item xs={12}>
			<Card sx={{ p: 3 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
					<ToggleButtonGroup size="small" exclusive value="values">
						<ToggleButton value="values">Price Values</ToggleButton>
						<ToggleButton value="variation">Variation</ToggleButton>
					</ToggleButtonGroup>
					<IconButton aria-label="Fullscreen" title="Fullscreen" size="small">
						<IconSeeker icon="expand" />
					</IconButton>
				</Box>

				<Box sx={{ height: 250, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', gap: 2, px: 2 }}>
					{DEMO.map((d, idx) => {
						const maxPrice = Math.max(...DEMO.map(x => x.prices[0]))
						const h = (d.prices[0] / maxPrice) * 200
						return <Box key={d.name} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flex: 1 }}>
							<Box sx={{ width: '100%', maxWidth: 60, height: h, bgcolor: COLORS[idx], borderRadius: '4px 4px 0 0', opacity: 0.8 }} />
							<Typography variant="caption" color="text.secondary">{d.name}</Typography>
						</Box>
					})}
				</Box>
			</Card>
		</Grid>

		{/* Real MUI Popover — only mounts when demoPopoverOpen signal is true */}
		<Popover
			open={!!anchorEl}
			anchorEl={anchorEl}
			onClose={() => {}}
			anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
			transformOrigin={{ vertical: 'center', horizontal: 'left' }}
			disableScrollLock
			PaperProps={{ 'data-tour-id': 'tour-demo-popover', sx: { p: 2, backgroundColor: 'white' } }}
		>
			<Box sx={{ minWidth: 220 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
					<Typography variant="body2" fontWeight={600}>Volkswagen Golf 1.6 TDI</Typography>
					<IconButton size="small" disabled sx={{ ml: 1 }}>
						<IconSeeker icon="xmark" />
					</IconButton>
				</Box>
				<Divider sx={{ my: 0.5 }} />
				<Box sx={{ py: 0.5 }}>
					{[
						{ label: 'Category', value: 'Economy' },
						{ label: 'Fuel', value: 'Full to Full' },
						{ label: 'Payment', value: 'Prepaid' },
						{ label: 'PickUp', value: '12 May 2025' },
					].map(({ label, value }) => (
						<Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25, gap: 2 }}>
							<Typography variant="caption" color="text.secondary">{label}</Typography>
							<Typography variant="caption" fontWeight={500}>{value}</Typography>
						</Box>
					))}
				</Box>
				<Divider sx={{ my: 0.5 }} />
				<Box sx={{ pt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
					{[
						{ icon: 'chart-line', color: 'success.main', title: 'Evolution Chart', tourId: 'tour-popover-evolution' },
						{ icon: 'magnifying-glass', color: 'warning.main', title: 'Real-Time Searches', tourId: 'tour-popover-realtime' },
						{ icon: 'image', color: 'info.main', title: 'Screenshot', tourId: 'tour-popover-screenshot' },
					].map(({ icon, color, title, tourId }) => (
						<Box
							key={icon}
							title={title}
							data-tour-id={tourId}
							sx={{
								display: 'flex', alignItems: 'center', justifyContent: 'center',
								borderRadius: '5px', border: '1px solid', width: 22, height: 22,
								borderColor: color, cursor: 'default',
							}}
						>
							<Box sx={{ color, fontSize: '0.75rem', lineHeight: 1 }}>
								<IconSeeker icon={icon} />
							</Box>
						</Box>
					))}
				</Box>
			</Box>
		</Popover>
	</Grid>
}
